import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { CustomUserDTO } from '@/types/api-types';

import { Select, SelectContent, SelectItem } from '@/components/ui/select';

import { useGlobalStore } from '@/stores/globalStore';

import { apiService } from '@/services/api-service';

import { USER_ROLES } from '@/constants/global-constants';

interface UserSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  userTypes?: string[]; // Allow filtering by specific user types
  usersUuidsFromAgreement: string[];
  children?: React.ReactNode;
}

export const UserSelect: FC<UserSelectProps> = ({
  value,
  onValueChange,
  usersUuidsFromAgreement,
  userTypes = [USER_ROLES.EXECUTOR.value, USER_ROLES.CONTROLLER.value],
  children,
}) => {
  const { getAutoFetchUsers } = useGlobalStore();
  const [users, setUsers] = useState<CustomUserDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalUserUuids, setTotalUserUuids] = useState<string[]>([]);
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  const BATCH_SIZE = 50;

  // Initial load - get all user UUIDs and load first batch
  useEffect(() => {
    const fetchInitialUsers = async () => {
      setIsLoading(true);
      try {
        // First, search for ALL user UUIDs with the specified roles via ElasticSearch
        const fetchedUserUuids = await searchUsersByRoles(userTypes);

        const allUserUuids = [
          ...new Set([...usersUuidsFromAgreement, ...fetchedUserUuids]),
        ];

        setTotalUserUuids(allUserUuids);
        // Then fetch the first batch of actual user data
        const firstBatchUuids = allUserUuids.slice(0, BATCH_SIZE);
        const fetchedUsers = await getAutoFetchUsers(firstBatchUuids);

        setUsers(fetchedUsers);
        setCurrentPage(1);
        setHasMore(allUserUuids.length > BATCH_SIZE);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setUsers([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialUsers();
  }, []); // Removed getAutoFetchUsers from dependencies as store functions should be stable

  const searchUsersByRoles = useCallback(
    async (roles: string[]): Promise<string[]> => {
      try {
        // Use match queries (proven to work)
        const query = {
          query: {
            bool: {
              must: [
                {
                  term: {
                    table: 'users',
                  },
                },
                {
                  bool: {
                    should: roles.map((role) => ({
                      match: {
                        public_users_user_type: role,
                      },
                    })),
                    minimum_should_match: 1,
                  },
                },
              ],
            },
          },
          size: 10000,
          _source: ['public_users_uuid'],
        };

        const response = await apiService.elasticSearch(query);
        if (response.hits.hits.length === 0) {
          throw new Error('No users found');
        }
        return response.hits.hits.map((hit) => hit._source.public_users_uuid);
      } catch (error) {
        console.error('ElasticSearch failed:', error);
        throw error;
      }
    },
    [],
  ); // Empty dependency array since this function doesn't depend on any props/state

  const loadMoreUsers = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading) return;

    setIsLoadingMore(true);
    try {
      const startIndex = currentPage * BATCH_SIZE;
      const endIndex = startIndex + BATCH_SIZE;
      const nextBatchUuids = totalUserUuids.slice(startIndex, endIndex);

      if (nextBatchUuids.length === 0) {
        setHasMore(false);
        return;
      }

      const fetchedUsers = await getAutoFetchUsers(nextBatchUuids);

      setUsers((prevUsers) => [...prevUsers, ...fetchedUsers]);
      setCurrentPage((prevPage) => prevPage + 1);
      setHasMore(endIndex < totalUserUuids.length);
    } catch (error) {
      console.error('Failed to load more users:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasMore,
    isLoadingMore,
    isLoading,
    currentPage,
    totalUserUuids,
    getAutoFetchUsers,
  ]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      // Check if scrolled to bottom (with small threshold)
      if (scrollHeight - scrollTop <= clientHeight + 5) {
        loadMoreUsers();
      }
    },
    [loadMoreUsers],
  );

  return (
    <Select onValueChange={onValueChange} value={value} disabled={isLoading}>
      {children}
      <SelectContent>
        <div
          className="max-h-60 overflow-y-auto"
          onScroll={handleScroll}
          ref={scrollViewportRef}
        >
          {users.map((u) => (
            <SelectItem key={u.uuid} value={u.uuid}>
              <span>
                {u.first_name} {u.last_name}
              </span>
            </SelectItem>
          ))}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-2">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="h-4 w-4 animate-spin-slow rounded-full border-2 border-transparent border-l-primary border-t-primary"></div>
                <span>завантаження користувачів...</span>
              </div>
            </div>
          )}
          {!hasMore && users.length > 0 && (
            <div className="flex items-center justify-center py-2">
              <span className="text-sm text-gray-400">
                Всі користувачі завантажені ({users.length} всього)
              </span>
            </div>
          )}
        </div>
      </SelectContent>
    </Select>
  );
};
